import classNames from 'classnames';
import { Button } from 'components/atoms/Button';
import { Tooltip } from 'components/atoms/Tooltip';
import { AnimatePresence, motion } from 'motion/react';
import { useCallback } from 'react';
import AIChatSpinner from '../AIChat/AIChatMessageSkeleton';

type AnimationOriginType =
  | 'bottom-left'
  | 'bottom-right'
  | 'top-left'
  | 'top-right';

export type AIHelperProps = {
  tooltip?: string;
  className?: string;
  children?: React.ReactNode;
  isVisible?: boolean;
  onVisibilityChange?: (isVisible: boolean) => void;
  size?: 'small' | 'medium' | 'large';
  animationOrigin?: AnimationOriginType;
  hideOpenButton?: boolean;
};

const getAnimationConfig = (origin: AnimationOriginType) => ({
  transition: { duration: 0.2 },
  originX: origin === 'top-left' || origin === 'bottom-left' ? '0%' : '100%',
  originY: origin === 'top-left' || origin === 'top-right' ? '0%' : '100%',
});

const AIHelper = ({
  className,
  tooltip,
  children,
  isVisible,
  onVisibilityChange,
  animationOrigin = 'top-right',
  size = 'medium',
  hideOpenButton = false,
}: AIHelperProps): JSX.Element => {
  const animationConfig = getAnimationConfig(animationOrigin);

  const onClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onVisibilityChange?.(!isVisible);
    },
    [isVisible, onVisibilityChange],
  );

  return (
    <div className={classNames('AIHelper', className)}>
      {!hideOpenButton && (
        <Tooltip content={tooltip}>
          <Button type="button" onClick={onClick} asDefault>
            <AIChatSpinner size={size} />
          </Button>
        </Tooltip>
      )}

      <AnimatePresence>
        {isVisible && (
          <motion.div
            initial={{ scale: 0.5, opacity: 0, ...animationConfig }}
            animate={{ scale: 1, opacity: 1, ...animationConfig }}
            exit={{ scale: 0.5, opacity: 0, ...animationConfig }}
            className={classNames('absolute z-50', {
              'top-0 right-0': animationOrigin === 'top-right',
              'top-0 left-0': animationOrigin === 'top-left',
              'bottom-0 right-0': animationOrigin === 'bottom-right',
              'bottom-0 left-0': animationOrigin === 'bottom-left',
            })}
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AIHelper;
